import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  IMPRESSIONS,
  Metric,
  ROAS,
} from "@front/m19-metrics";
import { AdStatsEx, StrategyGroupEx } from "@front/m19-models";
import {
  AccountSelectionService,
  MetricsSelectorPage,
  MetricsSelectorKey,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import { ChartConfig, IChartComponent, Option } from "@front/m19-ui";
import { DateAggregation, mergeSeveralDates, sumAdStats } from "@front/m19-utils";
import { ChartLayoutComponent } from "@m19-board/containers/chart-layout/chart-layout.component";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import { combineLatest, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import {
  ActivityEventAnnotationsService,
  ActivityEventType,
} from "../../../activities/activity-event-annotations.service";

@Component({
  selector: "app-sp-strategy-stats-graph",
  imports: [ChartLayoutComponent, MetricSelectorComponent, IChartComponent],
  templateUrl: "./sp-strategy-stats-graph.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpStrategyStatsGraphComponent {
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private readonly statsService = inject(StatsService);

  protected readonly CHART_METRICS: Metric<AdStatsEx>[] = [
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    IMPRESSIONS,
    CLICK_THROUGH_RATE,
    CONVERSION_RATE,
    CPC,
    ROAS,
  ];
  protected readonly localStorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.overview };

  public readonly strategyGroup = input.required<StrategyGroupEx>();

  protected readonly displayEventAnnotation = signal<boolean>(false);
  protected readonly allEventAnnotationTypes: Option<ActivityEventType>[] =
    this.activityEventAnnotationsService.allActivityEventTypesOptions;
  protected readonly dateAggregation = signal<DateAggregation>(DateAggregation.daily);
  readonly disableEventAnnotation = computed(() => this.dateAggregation() !== DateAggregation.daily);
  private readonly dataRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });
  private readonly eventsAnnotations = toSignal(
    toObservable(this.strategyGroup).pipe(
      switchMap((sg) => this.activityEventAnnotationsService.getStrategiesActivityEventAnnotation(sg!.strategies)),
    ),
  );
  private readonly periodComparison = toSignal(this.userSelectionService.periodComparison$);
  private readonly selectedMetrics = signal<Metric<AdStatsEx>[]>([AD_SALES, COST]);
  private readonly strategyIds = computed(
    () => new Set(this.strategyGroup()?.strategies?.map((s) => s.strategyId) ?? []),
  );

  readonly chartConfig = computed<ChartConfig<AdStatsEx, "line">>(() => ({
    type: "line",
    aspectRatio: 3,
    aggFunctions: [mergeSeveralDates],
    metrics: this.selectedMetrics(),
    dateAggregation: this.dateAggregation(),
    dateRange: this.dataRange(),
    previousPeriodData: this.previousPeriodData(),
    previousPeriod: this.periodComparison()?.period,
    eventAnnotations:
      this.displayEventAnnotation() && this.dateAggregation() === DateAggregation.daily
        ? this.eventsAnnotations()
        : undefined,
  }));

  private readonly dailyStats = toSignal(
    combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, dr, currency]) =>
        this.statsService.getDailyCampaignStats(am.accountId, am.marketplace, currency, dr[0], dr[1]),
      ),
    ),
  );
  readonly strategyDailyStats = computed(() => {
    return (this.dailyStats() ?? []).filter((d) => this.strategyIds().has(d.strategyId!));
  });
  protected readonly globalData = computed(() => {
    if (this.strategyDailyStats()) {
      return this.strategyDailyStats()!
        .filter((d) => this.strategyIds().has(d.strategyId!))
        .reduce((acc, curr) => sumAdStats(acc, curr), {} as AdStatsEx);
    }
    return undefined;
  });

  private readonly dailyStatsPeriodComparison = toSignal(
    combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.periodComparison$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, pc, currency]) =>
        pc?.period
          ? this.statsService.getDailyCampaignStats(am.accountId, am.marketplace, currency, pc.period[0], pc!.period[1])
          : of([]),
      ),
    ),
  );
  private readonly previousPeriodData = computed(() => {
    return (this.dailyStatsPeriodComparison() ?? []).filter((d) => this.strategyIds().has(d.strategyId!));
  });
  protected readonly previousPeriodGlobalData = computed(() => {
    if (this.previousPeriodData())
      return this.previousPeriodData()!.reduce((acc, curr) => sumAdStats(acc, curr), {} as AdStatsEx);
    return undefined;
  });

  protected selectMetrics(metrics: Metric<AdStatsEx>[]): void {
    this.selectedMetrics.set(metrics);
  }

  protected selectAggregation(aggregation: DateAggregation): void {
    this.dateAggregation.set(aggregation);
  }

  protected toggleEventAnnotation(checked: boolean): void {
    this.displayEventAnnotation.set(checked);
  }
}
